import { z } from 'zod';
import { UebernahmeStatus } from '../enums.js';
import { DokumentPflicht, DokumentRelevanz, DokumentVerbindlichkeit, KatalogFreigabeStatus, dokumentTyp, katalogFreigabe, } from '../dokumentkatalog.js';
/** Antwort von `GET /uebernahme/_katalog`: der Katalog samt Freigabestand. */
export const dokumentKatalogResponse = z.object({
    version: z.string(),
    lastVerified: z.string(),
    freigabe: katalogFreigabe,
    dokumente: z.array(dokumentTyp),
});
/**
 * Übernahme-Checkliste — die Soll-Liste der Unterlagen je Liegenschaft.
 *
 * Wer ein Objekt als Brandschutzbeauftragter übernimmt, muss wissen, welche
 * Unterlagen es gibt und welche fehlen. Jeder Eintrag verweist auf einen Typ
 * aus `dokumentkatalog.json` und trägt seinen Stand. Fehlt eine Unterlage,
 * gehört dazu die Stellungnahme des Auftraggebers (warum sie nicht existiert)
 * und die Bewertung des Betreuers (was daraus folgt) — sonst steht nur ein
 * rotes Feld ohne Erklärung in der Akte.
 */
export const uebernahmeDokument = z.object({
    id: z.number().int().positive(),
    liegenschaftId: z.number().int().positive(),
    /** Schlüssel aus `dokumentkatalog.json`. */
    katalogKey: z.string(),
    status: UebernahmeStatus,
    /** Bei `vorhanden`: das hochgeladene Dokument. */
    documentId: z.number().int().positive().nullable().optional(),
    /** Stellungnahme des Auftraggebers, warum die Unterlage fehlt. */
    stellungnahme: z.string().nullable().optional(),
    /**
     * Bewertung des Betreuers, ob und wie das Fehlen vertretbar ist. Sie ersetzt
     * keine Gefährdungsbeurteilung — sie hält fest, was der Betreuer entschieden
     * hat und warum.
     */
    bewertung: z.string().nullable().optional(),
    bewertetVonId: z.number().int().positive().nullable().optional(),
    bewertetAm: z.string().datetime().nullable().optional(),
    angefordertAm: z.string().datetime().nullable().optional(),
    notiz: z.string().nullable().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/**
 * PATCH /uebernahme/:id — Partial-Update (nullish): Feld fehlt = unverändert,
 * `null` = bewusst leeren. Die Statusregeln (vorhanden braucht ein Dokument,
 * Bewertung setzt den Bewerter) prüft das Backend in `logic.ts`.
 */
export const updateUebernahmeDokumentRequest = z.object({
    status: UebernahmeStatus.optional(),
    documentId: z.number().int().positive().nullable().optional(),
    stellungnahme: z.string().trim().max(5000).nullable().optional(),
    bewertung: z.string().trim().max(5000).nullable().optional(),
    notiz: z.string().trim().max(5000).nullable().optional(),
});
/** Zähler über die Liste — die Grundlage des Fortschrittsbalkens. */
export const uebernahmeFortschritt = z.object({
    gesamt: z.number().int().nonnegative(),
    vorhanden: z.number().int().nonnegative(),
    fehlt: z.number().int().nonnegative(),
    angefordert: z.number().int().nonnegative(),
    offen: z.number().int().nonnegative(),
    nichtZutreffend: z.number().int().nonnegative(),
});
/**
 * Eintrag, wie die Oberfläche ihn zeigt: der Datensatz plus die Katalogdaten
 * (Titel, Grundlage, Bedingung), damit die Liste ohne zweiten Aufruf lesbar ist.
 */
export const uebernahmeDokumentZeile = uebernahmeDokument.extend({
    titel: z.string(),
    beschreibung: z.string(),
    grundlage: z.string(),
    quelleUrl: z.string(),
    verbindlichkeit: DokumentVerbindlichkeit,
    pflicht: DokumentPflicht,
    bedingung: z.string().nullable(),
    /** Warum der Eintrag in der Liste steht — siehe `DokumentRelevanz`. */
    relevanz: DokumentRelevanz,
    /** Dateiname und MIME-Typ des verknüpften Dokuments, falls vorhanden — für die Vorschau. */
    documentFilename: z.string().nullable(),
    documentMimeType: z.string().nullable(),
    bewertetVonName: z.string().nullable(),
    /** `true`, wenn der Katalog den Schlüssel nicht mehr kennt — der Eintrag bleibt lesbar, aber ohne Fundstelle. */
    katalogUnbekannt: z.boolean(),
});
export const uebernahmeListeResponse = z.object({
    items: z.array(uebernahmeDokumentZeile),
    fortschritt: uebernahmeFortschritt,
    datenstand: z.object({
        version: z.string(),
        lastVerified: z.string(),
        freigabe: KatalogFreigabeStatus,
    }),
});
export const uebernahmeAnlegenResponse = z.object({
    /** Wie viele Einträge neu entstanden sind — bestehende werden nicht angefasst. */
    angelegt: z.number().int().nonnegative(),
    vorhanden: z.number().int().nonnegative(),
});
